"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { catalogShelf, localized, type ShelfSection } from "@/lib/api";
import { ResultCard } from "@/app/components/ResultCard";
import { Carosello } from "@/app/components/Carosello";

/**
 * Lo scaffale: un reparto per fila, i prodotti dentro la fila.
 *
 * Esiste per chi arriva senza sapere cosa cercare. La ricerca conversazionale
 * presuppone una domanda già formata — «componenti in alluminio per automotive,
 * piccoli lotti» — e chi quella domanda non ce l'ha, davanti a un campo vuoto,
 * se ne va.
 *
 * **La pagina scorre in verticale, le file in orizzontale.** Sono due assi che
 * fanno due lavori diversi: scendendo si cambia reparto, scorrendo si guarda
 * dentro un reparto. La versione a griglia li mescolava, e soprattutto mostrava
 * una copertina per reparto invece della merce — un'insegna al posto dello
 * scaffale.
 *
 * **Nessun reparto vuoto.** Le sezioni arrivano già filtrate dal database: una
 * senza prodotti non esce. Un negozio con sedici insegne e otto prodotti non
 * sembra grande, sembra rotto, e ogni insegna vuota è un clic nel nulla.
 */

/** Apre il reparto accanto alla chat, ma resta un link vero verso la sua pagina. */
function apri(
  sezione: ShelfSection,
  onApri: (s: ShelfSection) => void,
): (e: React.MouseEvent) => void {
  return (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onApri(sezione);
  };
}

function Fila({
  sezione,
  onApri,
}: {
  sezione: ShelfSection;
  onApri: (s: ShelfSection) => void;
}) {
  const prodotti = sezione.products ?? [];

  if (prodotti.length === 0) return null;

  const sottotitolo = localized(sezione.subtitle);
  const titolo = localized(sezione.title);

  return (
    <section className="mt-9 first:mt-0" aria-label={titolo}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <Link
            href={`/catalogo/${sezione.slug}`}
            onClick={apri(sezione, onApri)}
            className="text-base font-semibold tracking-tight hover:text-accent"
          >
            {titolo}
          </Link>
          {sottotitolo && <span className="text-xs text-muted">{sottotitolo}</span>}
        </div>

        <div className="flex items-center gap-2">
          {sezione.products_count > prodotti.length && (
            <Link
              href={`/catalogo/${sezione.slug}`}
              onClick={apri(sezione, onApri)}
              className="text-xs text-accent underline-offset-4 hover:underline"
            >
              tutti i {sezione.products_count}
            </Link>
          )}
        </div>
      </div>

      <Carosello etichetta={titolo}>
        {prodotti.map((p) => (
          <li key={p.product_id} className="w-44 shrink-0 sm:w-48">
            <ResultCard hit={p} price={p.price} />
          </li>
        ))}
      </Carosello>
    </section>
  );
}

export function Scaffale({
  onApri,
  className = "",
}: {
  onApri: (s: ShelfSection) => void;
  className?: string;
}) {
  const [sezioni, setSezioni] = useState<ShelfSection[]>([]);

  useEffect(() => {
    let vivo = true;
    // Otto per fila: quanti ne servono perché su uno schermo largo la fila
    // continui oltre il bordo e si veda che continua. Il resto sta nella
    // pagina del reparto.
    catalogShelf(8)
      .then((d) => vivo && setSezioni(d.results))
      .catch(() => {
        // Lo scaffale è un aiuto, non la pagina: se l'API non risponde, la
        // ricerca resta lì e funziona.
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (sezioni.length === 0) return null;

  return (
    <div className={className}>
      <h2 className="mb-4 px-1 text-sm font-medium">Oppure guarda cosa c&apos;è</h2>
      {sezioni.map((s) => (
        <Fila key={s.slug} sezione={s} onApri={onApri} />
      ))}
    </div>
  );
}
